/**
 * Utility functions for category operations
 */

/**
 * Convert a string to a URL-friendly slug
 * @param {string} str - String to convert
 * @returns {string} URL-friendly slug
 */
export const createSlug = (str) => {
  if (!str) return '';
  return str
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
};

/**
 * Build hierarchical category slug path
 * Traverses up the parent chain to build full path: maincategory/subcategory/childrencategory
 * @param {Object} category - Category object with parent information
 * @param {Array} allCategories - All categories array for parent lookup
 * @returns {string} Hierarchical slug path (e.g., "maincategory/subcategory/childrencategory")
 */
export const getHierarchicalCategorySlug = (category, allCategories = []) => {
  if (!category) return '';
  
  // If category already has a hierarchical slug, use it
  if (category.fullPath) return category.fullPath;
  
  // Build path array starting from current category
  const pathParts = [];
  let currentCategory = category;
  const visitedIds = new Set(); // Prevent infinite loops
  
  // Traverse up the parent chain
  while (currentCategory && !visitedIds.has(currentCategory._id || currentCategory.id)) {
    visitedIds.add(currentCategory._id || currentCategory.id);
    
    // Get slug for current category
    const slug = currentCategory.slug || createSlug(currentCategory.name);
    if (slug) {
      pathParts.unshift(slug); // Add to beginning of array
    }
    
    // Get parent category
    const parentId = currentCategory.parentCategory?._id || 
                     currentCategory.parentCategory || 
                     currentCategory.parent?._id || 
                     currentCategory.parent;
    
    if (!parentId) break; // No parent, we've reached the top
    
    // Find parent in allCategories array
    if (allCategories && allCategories.length > 0) {
      currentCategory = allCategories.find(cat => 
        (cat._id || cat.id) === parentId || 
        String(cat._id || cat.id) === String(parentId)
      );
      
      if (!currentCategory) break; // Parent not found
    } else {
      // If allCategories not provided, try to use populated parent
      currentCategory = currentCategory.parentCategory || currentCategory.parent;
      if (!currentCategory || typeof currentCategory !== 'object') break;
    }
  }
  
  // Join path parts with forward slash
  return pathParts.join('/');
};

/**
 * Get category slug (backward compatible)
 * Returns hierarchical path if available, otherwise returns simple slug
 * @param {Object} category - Category object
 * @param {Array} allCategories - All categories array for parent lookup
 * @returns {string} Category slug or hierarchical path
 */
export const getCategorySlug = (category, allCategories = []) => {
  if (!category) return '';
  
  // Try hierarchical slug first
  const hierarchicalSlug = getHierarchicalCategorySlug(category, allCategories);
  if (hierarchicalSlug) return hierarchicalSlug;
  
  // Fallback to simple slug
  if (category.slug) return category.slug;
  
  // Generate slug from name
  return createSlug(category.name);
};

/**
 * Depth from root: 0 = main (no parent), 1 = sub (parent is main), 2+ = children / deeper levels.
 * @param {Object} category
 * @param {Array} allCategories - Full list for parent lookup
 * @returns {number}
 */
export const getCategoryDepth = (category, allCategories = []) => {
  if (!category || !Array.isArray(allCategories) || allCategories.length === 0) return 0;
  let depth = 0;
  let current = category;
  const visited = new Set();
  while (current) {
    const id = current._id || current.id;
    if (id != null && visited.has(String(id))) break;
    if (id != null) visited.add(String(id));
    const parentId =
      current.parentCategory?._id ||
      current.parentCategory ||
      current.parent?._id ||
      current.parent;
    if (!parentId) return depth;
    depth += 1;
    current = allCategories.find((c) => String(c._id || c.id) === String(parentId));
    if (!current) break;
  }
  return depth;
};

/**
 * Parent category id (string) for tree navigation and product fallback queries.
 */
export const getParentCategoryId = (category) => {
  if (!category) return null;
  if (category.parentCategory) {
    if (typeof category.parentCategory === 'object' && category.parentCategory._id) {
      return category.parentCategory._id;
    }
    if (typeof category.parentCategory === 'object') {
      return category.parentCategory.id || category.parentCategory;
    }
    return category.parentCategory;
  }
  if (category.parent) {
    if (typeof category.parent === 'object' && category.parent._id) {
      return category.parent._id;
    }
    if (typeof category.parent === 'object') {
      return category.parent.id || category.parent;
    }
    return category.parent;
  }
  return null;
};

/**
 * Collect category / subcategory ids stored on a product (APIs vary).
 */
export const extractProductCategoryIds = (product) => {
  const ids = new Set();
  const add = (v) => {
    if (v == null || v === '') return;
    if (typeof v === 'object') {
      if (v._id != null) ids.add(String(v._id));
      if (v.id != null) ids.add(String(v.id));
      return;
    }
    ids.add(String(v).trim());
  };
  if (!product || typeof product !== 'object') return ids;
  add(product.category);
  add(product.subcategory);
  add(product.childrenCategory);
  if (product.categoryId != null) add(product.categoryId);
  if (product.subCategoryId != null) add(product.subCategoryId);
  if (Array.isArray(product.categories)) product.categories.forEach(add);
  return ids;
};

export const productBelongsToCategoryId = (product, categoryId) => {
  if (categoryId == null || categoryId === '') return false;
  const cid = String(categoryId).trim();
  return extractProductCategoryIds(product).has(cid);
};

/**
 * Fixed display order for top-level main categories across the site
 * (header nav, mobile drawer, hero banner, /categories page, etc.).
 * Order: Women → Men → Kids → Accessories → Fragrances → Shoes.
 * Anything else falls in after these by name.
 */
export const getMainCategoryDisplayOrderRank = (cat) => {
  const n = String(cat?.name || '')
    .toLowerCase()
    .replace(/['']/g, '')
    .replace(/\s+/g, ' ')
    .trim();

  const hasClothingWord =
    n.includes('cloth') || n.includes('wear') || n.includes('dress') || n.includes('garment');
  const isWomen =
    n.includes('women') || n.includes('woman') || n.includes('womens') || n.includes('ladies');
  const isMen =
    !isWomen &&
    (n.includes("men's") ||
      n.includes('mens') ||
      /^men\b/.test(n) ||
      n.startsWith('men ') ||
      (n.includes('men') && (n.includes('cloth') || n.includes('wear'))));
  const isKids =
    n.includes('kid') || n.includes('child') || n.includes('baby') || n.includes('infant');

  if (isWomen && (hasClothingWord || n.includes('women'))) return 0;
  if (isMen && (hasClothingWord || n.includes('men'))) return 1;
  if (isKids && (hasClothingWord || n.includes('kid'))) return 2;
  if (n.includes('accessor')) return 3;
  if (n.includes('fragran') || n.includes('perfume') || n.includes('scent')) return 4;
  if (n.includes('shoe') || n.includes('footwear') || n.includes('sandal')) return 5;
  return 100;
};

/**
 * Sort an array of main categories using the shared display order.
 * Stable: ties are broken by case-insensitive name compare.
 */
export const sortMainCategoriesForDisplay = (mains) => {
  if (!Array.isArray(mains)) return [];
  return [...mains].sort((a, b) => {
    const ra = getMainCategoryDisplayOrderRank(a);
    const rb = getMainCategoryDisplayOrderRank(b);
    if (ra !== rb) return ra - rb;
    return String(a?.name || '').localeCompare(String(b?.name || ''), undefined, {
      sensitivity: 'base',
    });
  });
};

/**
 * Resolve a storefront path like "mens/shirts" to the correct category document.
 * Prefers exact slug match, then longest path suffix (avoids matching "accessories" to "women/accessories").
 */
export const findCategoryForStorefrontPath = (categoriesData, categorySlug) => {
  if (!categorySlug || !Array.isArray(categoriesData) || categoriesData.length === 0) return null;
  const norm = String(categorySlug)
    .replace(/^\/+|\/+$/g, '')
    .toLowerCase();
  if (!norm) return null;

  const slugOf = (cat) => {
    const s = getCategorySlug(cat, categoriesData);
    return String(s || '')
      .replace(/^\/+|\/+$/g, '')
      .toLowerCase();
  };

  for (const cat of categoriesData) {
    if (slugOf(cat) === norm) return cat;
  }

  let best = null;
  let bestLen = -1;
  for (const cat of categoriesData) {
    const s = slugOf(cat);
    if (!s) continue;
    if (s === norm || s.endsWith(`/${norm}`)) {
      if (s.length > bestLen) {
        bestLen = s.length;
        best = cat;
      }
    }
  }
  return best;
};

